import { Injectable, computed, inject, signal } from '@angular/core';
import { Data } from '@angular/router';

import { AuthService } from '../../features/auth/services/auth.service';
import { getRoutePermissionData, hasAllPermissions, hasAnyPermission } from '../utils/permission.utils';
import { PlatformPermission } from '../models/permission.model';
import { PermissionMatchMode } from '../models/route-permission-data.model';
import { resolveId } from '../../core/utils/id.util';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private static readonly MASTER_ADMIN_ROLE_CODES = new Set<string>(['MASTER_ADMIN', 'SUPER_ADMIN']);
  private static readonly LEGACY_PERMISSION_FALLBACKS: Readonly<Record<string, ReadonlyArray<string>>> = {
    'users_create': ['users.create'],
    'users_edit': ['users.update'],
    'users_delete': ['users.delete'],
    'users_view': ['users.view'],
    'users_column_actions': ['users.view'],
    'schools_create': ['schools.create'],
    'schools_edit': ['schools.update'],
    'schools_delete': ['schools.delete'],
    'schools_view': ['schools.view'],
    'schools_column_actions': ['schools.view'],
    'organizations_create': ['organizations.create'],
    'organizations_edit': ['organizations.update'],
    'organizations_delete': ['organizations.delete'],
    'organizations_view': ['organizations.view'],
    'organizations_column_actions': ['organizations.view'],
    'students_create': ['students.create'],
    'students_edit': ['students.update'],
    'students_delete': ['students.delete'],
    'students_view': ['students.view'],
    'students_column_actions': ['students.view'],
    'teachers_create': ['teachers.create'],
    'teachers_edit': ['teachers.update'],
    'teachers_delete': ['teachers.delete'],
    'teachers_view': ['teachers.view'],
    'teachers_column_actions': ['teachers.view'],
    'teacher_assignments_create': ['teacherAssignments.create'],
    'teacher_assignments_update': ['teacherAssignments.update'],
    'teacher_assignments_delete': ['teacherAssignments.delete'],
    'teacher_assignments_view': ['teacherAssignments.view'],
    'teacher_assignments_column_actions': ['teacherAssignments.view'],
    'teacher_assigments_create': ['teacherAssignments.create'],
    'teacher_assigments_update': ['teacherAssignments.update'],
    'teacher_assigments_delete': ['teacherAssignments.delete'],
    'teacher_assigments_view': ['teacherAssignments.view'],
    'teacher_assigments_column_actions': ['teacherAssignments.view'],
    'sections_create': ['sections.create'],
    'sections_edit': ['sections.update'],
    'sections_delete': ['sections.delete'],
    'sections_view': ['sections.view'],
    'sections_column_actions': ['sections.view'],
    'enrollments_create': ['enrollments.create'],
    'enrollments_edit': ['enrollments.update'],
    'enrollments_delete': ['enrollments.delete'],
    'enrollments_view': ['enrollments.view'],
    'enrollments_column_actions': ['enrollments.view'],
    'courses_create': ['courses.create'],
    'courses_edit': ['courses.update'],
    'courses_delete': ['courses.delete'],
    'courses_view': ['courses.view'],
    'courses_column_actions': ['courses.view'],
    'academic_years_create': ['academicYears.create'],
    'academic_years_update': ['academicYears.update'],
    'academic_years_edit': ['academicYears.update'],
    'academic_years_delete': ['academicYears.delete'],
    'academic_years_view': ['academicYears.view'],
    'academic_years_column_actions': ['academicYears.view']
  };

  private readonly authService = inject(AuthService);
  private readonly lastPermissionError = signal<string | null>(null);

  readonly permissions = computed<ReadonlyArray<PlatformPermission>>(() => {
    try {
      const resolvedPermissions = this.resolvePermissionCodes();

      if (this.lastPermissionError() && resolvedPermissions.length > 0) {
        this.lastPermissionError.set(null);
      }
      return resolvedPermissions;
    } catch (error) {
      const message = this.formatErrorMessage(error, 'Failed to resolve permission codes.');
      this.lastPermissionError.set(message);
      return [];
    }
  });

  readonly debugState = computed(() => ({
    permissionCount: this.permissions().length,
    isMasterAdmin: this.isMasterAdmin(),
    roleCode: this.getCurrentRoleCode(),
    lastError: this.lastPermissionError()
  }));

  getCurrentRoleCode(): string | null {
    return this.resolveRoleCode(this.authService.currentUser());
  }

  // The role CODE is the only thing that identifies a master admin.
  //
  // This used to also accept role id 6 as a fallback for payloads that arrived
  // without a code. That worked only because role ids were sequential and the
  // seeder happened to insert MASTER_ADMIN sixth — the id carries no meaning now
  // that roles are keyed by uuid, and no fixed value can replace it. A payload
  // with no role code is simply not a master admin.
  isMasterAdmin(): boolean {
    const roleCode = this.resolveRoleCode(this.authService.currentUser());

    return roleCode !== null && PermissionService.MASTER_ADMIN_ROLE_CODES.has(roleCode);
  }

  isCurrentUserMasterAdmin(): boolean {
    return this.isMasterAdmin();
  }

  // ADMIN is the organization-scoped administrator role (never master admin).
  isCurrentUserAdmin(): boolean {
    const roleCode = this.getCurrentRoleCode();
    return roleCode !== null && roleCode.trim().toUpperCase() === 'ADMIN';
  }

  // DIRECTOR is a normal, permission-gated role (never treated as master admin).
  // These helpers only expose its school scope so the UI can lock org/school.
  isCurrentUserDirector(): boolean {
    const roleCode = this.getCurrentRoleCode();
    return roleCode !== null && roleCode.trim().toUpperCase() === 'DIRECTOR';
  }

  isCurrentUserTeacher(): boolean {
    const roleCode = this.getCurrentRoleCode();
    return roleCode !== null && roleCode.trim().toUpperCase() === 'TEACHER';
  }

  // PARENT is a self-scoped role bound to its own children: its home is the
  // per-child landing (/home/parents), and every feature reached from there is
  // strictly read-only (notes locked, attendance switches disabled, breadcrumb
  // navigation disabled).
  isCurrentUserParent(): boolean {
    const roleCode = this.getCurrentRoleCode();
    return roleCode !== null && roleCode.trim().toUpperCase() === 'PARENT';
  }

  // STUDENT is self-scoped to its OWN data (a single student = itself). It shares
  // the PARENT read-only landing/experience.
  isCurrentUserStudent(): boolean {
    const roleCode = this.getCurrentRoleCode();
    return roleCode !== null && roleCode.trim().toUpperCase() === 'STUDENT';
  }

  // PARENT and STUDENT share the same read-only, self-scoped home experience
  // (the /home/parents-style landing + locked courses/notes/attendance). PARENT
  // sees its children; STUDENT sees only itself.
  isCurrentUserSelfReadonly(): boolean {
    return this.isCurrentUserParent() || this.isCurrentUserStudent();
  }

  // School-scoped roles (DIRECTOR, TEACHER) are bound to a single school: their
  // home is the per-school view (/home/schools), not the org landing (/home), and
  // they cannot navigate up to it.
  isCurrentUserSchoolScoped(): boolean {
    return this.isCurrentUserDirector() || this.isCurrentUserTeacher();
  }

  getCurrentUserSchoolId(): string | null {
    const schoolId = this.authService.currentUser()?.schoolId;
    return resolveId(schoolId);
  }

  // The signed-in user's own id, for the checks that compare a record's owner
  // against "me" — e.g. whether a TEACHER is the one teaching a given course.
  // The session payload spells it `user_id` or `id` depending on which endpoint
  // filled it, so both are tried in that order.
  getCurrentUserId(): string | null {
    const currentUser = this.authService.currentUser();

    if (!currentUser) {
      return null;
    }

    return resolveId(currentUser.user_id) ?? resolveId(currentUser.id);
  }

  hasPermission(permissionCode: PlatformPermission): boolean {
    if (this.isMasterAdmin()) {
      return true;
    }

    const normalizedPermissionCode = this.normalizePermissionCode(permissionCode);

    if (!normalizedPermissionCode) {
      return false;
    }

    return this.permissions().includes(normalizedPermissionCode as PlatformPermission);
  }

  hasAnyPermission(permissionCodes: ReadonlyArray<PlatformPermission>): boolean {
    if (this.isMasterAdmin()) {
      return true;
    }

    if (!permissionCodes.length) {
      return true;
    }

    const normalizedUserPermissions = new Set(this.permissions());
    const expandedPermissionCodes = this.expandPermissionCodes(permissionCodes);
    return expandedPermissionCodes.some((code) => normalizedUserPermissions.has(code as PlatformPermission));
  }

  hasAllPermissions(permissionCodes: ReadonlyArray<PlatformPermission>): boolean {
    if (this.isMasterAdmin()) {
      return true;
    }

    if (!permissionCodes.length) {
      return true;
    }

    const normalizedUserPermissions = new Set(this.permissions());
    const expandedPermissionCodes = this.expandPermissionCodes(permissionCodes);
    return expandedPermissionCodes.every((code) => normalizedUserPermissions.has(code as PlatformPermission));
  }

  can(criteria: ReadonlyArray<PlatformPermission> | PlatformPermission, mode: PermissionMatchMode = 'all'): boolean {
    try {
      if (this.isMasterAdmin()) {
        return true;
      }

      const permissions = Array.isArray(criteria) ? criteria : [criteria];

      if (!permissions.length) {
        return true;
      }

      if (mode === 'any') {
        return permissions.some((permission) => this.canAny([permission]));
      }

      return permissions.every((permission) => this.canAny([permission]));
    } catch (error) {
      const message = this.formatErrorMessage(error, 'Permission check failed in can().');
      this.lastPermissionError.set(message);
      return false;
    }
  }

  canAny(permissionCodes: ReadonlyArray<string>): boolean {
    try {
      if (this.isMasterAdmin()) {
        return true;
      }

      if (!permissionCodes.length) {
        return true;
      }

      const expandedPermissions = this.expandPermissionCodes(permissionCodes);
      const userPermissions = this.permissions();

      return hasAnyPermission(userPermissions, expandedPermissions);
    } catch (error) {
      const message = this.formatErrorMessage(error, 'Permission check failed in canAny().');
      this.lastPermissionError.set(message);
      return false;
    }
  }

  canAll(permissionCodes: ReadonlyArray<string>): boolean {
    try {
      if (this.isMasterAdmin()) {
        return true;
      }

      if (!permissionCodes.length) {
        return true;
      }

      const expandedPermissions = this.expandPermissionCodes(permissionCodes);
      const userPermissions = this.permissions();

      return hasAllPermissions(userPermissions, expandedPermissions);
    } catch (error) {
      const message = this.formatErrorMessage(error, 'Permission check failed in canAll().');
      this.lastPermissionError.set(message);
      return false;
    }
  }

  canAccessRouteData(data: Data | undefined): boolean {
    try {
      const routePermissionData = getRoutePermissionData(data);

      if (!routePermissionData?.permissions?.length) {
        return true;
      }

      return this.can(routePermissionData.permissions, routePermissionData.mode ?? 'all');
    } catch (error) {
      const message = this.formatErrorMessage(error, 'Permission check failed in canAccessRouteData().');
      this.lastPermissionError.set(message);
      return false;
    }
  }

  canCreateAcademicYear(): boolean {
    return this.canAny(['ACADEMIC_YEARS_CREATE']);
  }

  canEditAcademicYear(): boolean {
    return this.canAny(['ACADEMIC_YEARS_EDIT']);
  }

  canDeleteAcademicYear(): boolean {
    return this.canAny(['ACADEMIC_YEARS_DELETE']);
  }

  canCreateCourse(): boolean {
    return this.canAny(['COURSES_CREATE']);
  }

  canEditCourse(): boolean {
    return this.canAny(['COURSES_EDIT']);
  }

  canDeleteCourse(): boolean {
    return this.canAny(['COURSES_DELETE']);
  }

  canManageRoles(): boolean {
    return this.canAny(['roles_manage', 'roles_view']);
  }

  canManageAcademicYears(): boolean {
    return this.canCreateAcademicYear() || this.canEditAcademicYear() || this.canDeleteAcademicYear();
  }

  canManageCourses(): boolean {
    return this.canCreateCourse() || this.canEditCourse() || this.canDeleteCourse();
  }

  canAccessAdminPermissions(): boolean {
    // The role-permissions matrix (/admin/permissions) is MASTER_ADMIN only.
    return this.isMasterAdmin();
  }

  private resolvePermissionCodes(): ReadonlyArray<PlatformPermission> {
    const currentUser = this.authService.currentUser();
    const currentUserRecord = this.toRecord(currentUser);
    const backendPermissions = this.collectPermissionCodesToArray(currentUserRecord?.['permissions']);

    if (backendPermissions.length > 0) {
      return backendPermissions;
    }

    return [];
  }

  private resolveRoleCode(currentUser: unknown): string | null {
    const currentUserRecord = this.toRecord(currentUser);

    if (!currentUserRecord) {
      return null;
    }

    const roleRecord = this.toRecord(currentUserRecord['role']);
    const userRoles = Array.isArray(currentUserRecord['roles']) ? currentUserRecord['roles'] : [];
    const firstUserRole = userRoles.length > 0 ? this.resolveString(userRoles[0]) : null;
    const roleCodeCandidate =
      this.resolveString(roleRecord?.['code']) ??
      this.resolveString(currentUserRecord['role_code']) ??
      this.resolveString(currentUserRecord['roleCode']) ??
      this.resolveString(currentUserRecord['role']) ??
      this.resolveString(roleRecord?.['name']) ??
      this.resolveString(currentUserRecord['role_name']) ??
      this.resolveString(currentUserRecord['roleName']) ??
      firstUserRole;

    if (!roleCodeCandidate) {
      return null;
    }

    return this.normalizeRoleCode(roleCodeCandidate);
  }

  private resolveRoleId(currentUser: unknown): string | null {
    const currentUserRecord = this.toRecord(currentUser);

    if (!currentUserRecord) {
      return null;
    }

    const roleRecord = this.toRecord(currentUserRecord['role']);

    return (
      resolveId(currentUserRecord['role_id']) ??
      resolveId(currentUserRecord['roleId']) ??
      resolveId(currentUserRecord['roleID']) ??
      resolveId(roleRecord?.['id']) ??
      null
    );
  }

  private normalizeRoleCode(value: string): string {
    return value.trim().toUpperCase().replace(/[\s-]+/g, '_');
  }

  private collectPermissionCodesToArray(source: unknown): ReadonlyArray<PlatformPermission> {
    const collectedPermissions = new Set<PlatformPermission>();
    this.collectPermissionCodes(source, collectedPermissions);
    return Array.from(collectedPermissions);
  }

  private collectPermissionCodes(source: unknown, sink: Set<PlatformPermission>): void {
    if (!source) {
      return;
    }

    if (typeof source === 'string') {
      const normalized = this.normalizePermissionCode(source);

      if (normalized) {
        sink.add(normalized);
      }

      return;
    }

    if (Array.isArray(source)) {
      for (const entry of source) {
        this.collectPermissionCodes(entry, sink);
      }
      return;
    }

    const sourceRecord = this.toRecord(source);

    if (!sourceRecord) {
      return;
    }

    const directCode =
      this.normalizePermissionCode(sourceRecord['permission_code']) ??
      this.normalizePermissionCode(sourceRecord['code']) ??
      this.normalizePermissionCode(sourceRecord['name']) ??
      this.normalizePermissionCode(sourceRecord['permission']) ??
      this.normalizePermissionCode(sourceRecord['authority']);

    if (directCode) {
      sink.add(directCode);
    }

    this.collectPermissionCodes(sourceRecord['permissions'], sink);
    this.collectPermissionCodes(sourceRecord['authorities'], sink);
  }

  private expandPermissionCodes(permissionCodes: ReadonlyArray<string>): ReadonlyArray<string> {
    const expanded = new Set<string>();

    for (const permissionCode of permissionCodes) {
      const normalizedCode = this.normalizePermissionCode(permissionCode);

      if (!normalizedCode) {
        continue;
      }

      expanded.add(normalizedCode);

      const legacyFallbacks = PermissionService.LEGACY_PERMISSION_FALLBACKS[normalizedCode] ?? [];

      for (const legacyCode of legacyFallbacks) {
        const normalizedLegacyCode = this.normalizePermissionCode(legacyCode);

        if (normalizedLegacyCode) {
          expanded.add(normalizedLegacyCode);
        }
      }
    }

    return Array.from(expanded);
  }

  private normalizePermissionCode(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const normalizedValue = value.trim().toLowerCase().replace(/[.\s-]+/g, '_');
    return normalizedValue.length > 0 ? normalizedValue : null;
  }

  private toRecord(value: unknown): Record<string, unknown> | null {
    return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
  }

  private resolveString(value: unknown): string | null {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'bigint') {
      return String(value);
    }

    return null;
  }

  private resolveNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      const parsedValue = Number(value);
      if (Number.isFinite(parsedValue)) {
        return parsedValue;
      }
    }

    return null;
  }

  private formatErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof Error && error.message.trim().length > 0) {
      return error.message;
    }

    if (typeof error === 'string' && error.trim().length > 0) {
      return error;
    }

    return fallback;
  }
}
